import type { WidgetLatest, WidgetLatestPage, WidgetVerdict } from "./widget-monitor.js";

/**
 * Pure shaping of `latest.json` for the Widgets page, kept out of the components
 * so it is unit-tested without a DOM.
 *
 * A target key is `<estate>/<brand>/<page…>`, plus a full-lane suffix: `@mobile`
 * (375px) or `#<look or ticker>` (`#lang=de&theme=dark`, `#ticker=tse:enb`), from
 * how customers load the page. The grid shows one cell per PAGE; its variants
 * live in the drawer.
 */
export const WIDGET_VERDICT_ORDER: WidgetVerdict[] = ["BROKEN", "THROTTLED", "DEGRADED", "WARN", "UNVERIFIABLE", "OK"];

export const widgetPageOf = (key: string) => key.replace(/[@#].*$/, "");
export const isWidgetVariantKey = (key: string) => /[@#]/.test(key);

/** Drawer title of a variant: `@mobile` → "Mobile 375px", `#lang=de&theme=dark` → "lang=de · theme=dark". */
export function widgetVariantLabel(key: string): string {
  if (key.endsWith("@mobile")) return "Mobile 375px";
  const look = key.slice(key.indexOf("#") + 1);
  return look === "default" ? "No look params" : look.split("&").join(" · ");
}

export interface WidgetCell {
  key: string;
  label: string;
  estate: string;
  page: WidgetLatestPage;
  /** Worst verdict across the page and its variants. */
  worst: WidgetVerdict;
  deepNotOk: boolean;
  variants: Array<{ key: string; page: WidgetLatestPage }>;
}

export interface WidgetBrandRow {
  brand: string;
  active: boolean;
  estates: string[];
  cells: WidgetCell[];
  worst: WidgetVerdict;
}

const rank = (v: WidgetVerdict) => WIDGET_VERDICT_ORDER.indexOf(v);
export const worstWidgetVerdict = (vs: WidgetVerdict[]) => vs.reduce<WidgetVerdict>((w, v) => (rank(v) < rank(w) ? v : w), "OK");

export function buildWidgetRows(latest: WidgetLatest | null, onlyProblems = false): WidgetBrandRow[] {
  if (!latest) return [];
  const byPage = new Map<string, WidgetCell>();
  const entries = Object.entries(latest.pages);
  for (const [key, page] of entries.filter(([k]) => !isWidgetVariantKey(k))) {
    const [estate, , ...rest] = key.split("/");
    byPage.set(key, { key, label: rest.join("/"), estate: estate!, page, worst: page.verdict, deepNotOk: false, variants: [] });
  }
  for (const [key, page] of entries.filter(([k]) => isWidgetVariantKey(k))) {
    byPage.get(widgetPageOf(key))?.variants.push({ key, page });
  }
  for (const c of byPage.values()) {
    c.worst = worstWidgetVerdict([c.page.verdict, ...c.variants.map((v) => v.page.verdict)]);
    c.deepNotOk = [c.page, ...c.variants.map((v) => v.page)].some((p) => p.deep && p.deep.verdict !== "OK");
  }

  const brands = new Map<string, WidgetCell[]>();
  for (const c of byPage.values()) brands.set(c.page.brand, [...(brands.get(c.page.brand) ?? []), c]);

  return [...brands]
    .map(([brand, cells]) => {
      const shown = onlyProblems ? cells.filter((c) => c.worst !== "OK" || c.deepNotOk) : cells;
      shown.sort((a, b) => Number(b.page.active) - Number(a.page.active) || rank(a.worst) - rank(b.worst) || a.label.localeCompare(b.label));
      return {
        brand,
        active: cells.some((c) => c.page.active),
        estates: [...new Set(cells.map((c) => c.estate))].sort(),
        cells: shown,
        worst: worstWidgetVerdict(cells.filter((c) => c.page.active).map((c) => c.worst)),
      };
    })
    .filter((r) => r.cells.length)
    .sort((a, b) => Number(b.active) - Number(a.active) || rank(a.worst) - rank(b.worst) || a.brand.localeCompare(b.brand));
}

export function tallyWidgetVerdicts(latest: WidgetLatest | null, activeOnly = true): Partial<Record<WidgetVerdict, number>> {
  const out: Partial<Record<WidgetVerdict, number>> = {};
  for (const [key, p] of Object.entries(latest?.pages ?? {})) {
    if (isWidgetVariantKey(key) || (activeOnly && !p.active)) continue;
    out[p.verdict] = (out[p.verdict] ?? 0) + 1;
  }
  return out;
}

/** "12 min ago" — the runAge of a run is the first thing to check on a monitor. */
export function runAge(iso: string | undefined, now = Date.now()): string {
  if (!iso) return "never";
  const min = Math.round((now - Date.parse(iso)) / 60_000);
  if (min < 1) return "just now";
  if (min < 90) return `${min} min ago`;
  const h = Math.round(min / 60);
  return h < 48 ? `${h} h ago` : `${Math.round(h / 24)} days ago`;
}

/**
 * Some lane finishes a run at least every ~8 hours (fast 06/14/22 UTC, full
 * 02:30 UTC; the longest gap is 06:00 to 14:00). GitHub starts scheduled
 * runs up to ~35 min late, so the page warns after MONITOR_STALE_MIN without any run.
 */
export const MONITOR_STALE_MIN = 600;

/** The newest of these ISO times; undefined when all are absent. */
export const newestRun = (...iso: (string | undefined)[]) =>
  iso.filter((x): x is string => !!x).sort((a, b) => Date.parse(b) - Date.parse(a))[0];

export const isRunStale = (iso: string | undefined, maxMin: number, now = Date.now()) =>
  !iso || now - Date.parse(iso) > maxMin * 60_000;
